import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { ArrowLeft } from "lucide-react";
import { useAuth } from "@/lib/auth";
import { useI18n } from "@/lib/i18n";
import { TEST_IDS } from "@/constants/testIds";

// REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
function startGoogleAuth() {
    const redirectUrl = window.location.origin + "/dashboard";
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
}

export default function AuthPage({ mode = "login" }) {
    const { t } = useI18n();
    const { login, register } = useAuth();
    const navigate = useNavigate();
    const [form, setForm] = useState({ email: "", password: "", name: "" });
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState("");

    async function onSubmit(e) {
        e.preventDefault();
        setError("");
        setLoading(true);
        try {
            if (mode === "register") {
                await register(form.email, form.password, form.name);
                toast.success("Workspace created — welcome!");
            } else {
                await login(form.email, form.password);
                toast.success("Welcome back");
            }
            navigate("/dashboard");
        } catch (err) {
            const msg = err?.response?.data?.detail || "Something went wrong";
            setError(msg);
        } finally {
            setLoading(false);
        }
    }

    const title = mode === "register" ? t.auth.registerTitle : t.auth.loginTitle;
    const sub = mode === "register" ? t.auth.registerSub : t.auth.loginSub;
    const cta = mode === "register" ? t.auth.register : t.auth.login;

    return (
        <div className="min-h-screen relative bg-brand-void text-white flex items-center justify-center px-6">
            <div className="absolute inset-0 radial-hero pointer-events-none" />
            <div className="absolute inset-0 bg-grid opacity-40 pointer-events-none" />
            <div className="absolute inset-0 bg-grain opacity-60 pointer-events-none" />

            <Link
                to="/"
                className="absolute top-6 left-6 inline-flex items-center gap-2 text-sm text-zinc-400 hover:text-white transition"
                data-testid="auth-back"
            >
                <ArrowLeft size={16} /> {t.auth.back}
            </Link>

            <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5 }}
                className="relative w-full max-w-md"
            >
                <div className="glass rounded-2xl p-8 sm:p-10 shadow-[0_30px_80px_-20px_rgba(99,102,241,0.4)]">
                    <div className="mb-8">
                        <div className="inline-flex items-center gap-2">
                            <span className="w-9 h-9 rounded-lg btn-cta flex items-center justify-center text-white font-black text-sm">B</span>
                            <span className="font-display font-black text-lg tracking-tight">BOXEB2B</span>
                        </div>
                        <h1 className="mt-6 font-display font-black text-3xl tracking-tighter">{title}</h1>
                        <p className="mt-2 text-sm text-zinc-400">{sub}</p>
                    </div>

                    <button
                        type="button"
                        onClick={startGoogleAuth}
                        data-testid={TEST_IDS.auth.googleBtn}
                        className="w-full flex items-center justify-center gap-3 px-4 py-3 rounded-lg bg-white text-brand-void font-medium text-sm hover:bg-zinc-100 transition"
                    >
                        <GoogleIcon />
                        {t.auth.google}
                    </button>

                    <div className="flex items-center gap-3 my-6">
                        <div className="h-px flex-1 bg-white/8" />
                        <span className="text-xs text-zinc-500">{t.auth.or}</span>
                        <div className="h-px flex-1 bg-white/8" />
                    </div>

                    <form onSubmit={onSubmit} className="space-y-4">
                        {mode === "register" && (
                            <Field
                                label={t.auth.name}
                                testId={TEST_IDS.auth.nameInput}
                                value={form.name}
                                onChange={(v) => setForm({ ...form, name: v })}
                                required
                                autoComplete="name"
                            />
                        )}
                        <Field
                            label={t.auth.email}
                            testId={TEST_IDS.auth.emailInput}
                            type="email"
                            value={form.email}
                            onChange={(v) => setForm({ ...form, email: v })}
                            required
                            autoComplete="email"
                        />
                        <Field
                            label={t.auth.password}
                            testId={TEST_IDS.auth.passwordInput}
                            type="password"
                            value={form.password}
                            onChange={(v) => setForm({ ...form, password: v })}
                            required
                            autoComplete={mode === "register" ? "new-password" : "current-password"}
                            hint={mode === "register" ? t.auth.passwordHint : ""}
                        />

                        {error && (
                            <div
                                data-testid={TEST_IDS.auth.error}
                                className="text-sm rounded-lg border border-red-500/30 bg-red-500/10 text-red-200 px-3 py-2"
                            >
                                {error}
                            </div>
                        )}

                        <button
                            type="submit"
                            disabled={loading}
                            data-testid={TEST_IDS.auth.submit}
                            className="w-full btn-cta text-white font-semibold py-3 rounded-lg disabled:opacity-70"
                        >
                            {loading ? "…" : cta}
                        </button>
                    </form>

                    <div className="mt-6 text-center text-sm text-zinc-500">
                        {mode === "register" ? t.auth.hasAccount : t.auth.noAccount}{" "}
                        <Link
                            to={mode === "register" ? "/login" : "/register"}
                            data-testid={TEST_IDS.auth.switchMode}
                            className="text-white hover:text-brand-cyan underline underline-offset-4"
                        >
                            {mode === "register" ? t.auth.signIn : t.auth.createOne}
                        </Link>
                    </div>

                    <p className="mt-8 text-[11px] text-zinc-600 leading-relaxed text-center">{t.auth.terms}</p>
                </div>
            </motion.div>
        </div>
    );
}

function Field({ label, testId, type = "text", value, onChange, required, autoComplete, hint }) {
    return (
        <label className="block">
            <span className="text-xs font-medium text-zinc-400">{label}</span>
            <input
                type={type}
                value={value}
                onChange={(e) => onChange(e.target.value)}
                required={required}
                autoComplete={autoComplete}
                data-testid={testId}
                className="mt-1.5 w-full px-3.5 py-2.5 rounded-lg bg-brand-void border border-white/10 focus:border-brand-indigo focus:ring-1 focus:ring-brand-indigo outline-none text-sm text-white placeholder:text-zinc-600 transition"
            />
            {hint && <span className="mt-1 block text-[11px] text-zinc-500">{hint}</span>}
        </label>
    );
}

function GoogleIcon() {
    return (
        <svg width="16" height="16" viewBox="0 0 48 48" xmlns="http://www.w3.org/2000/svg">
            <path fill="#EA4335" d="M24 9.5c3.54 0 6.71 1.22 9.21 3.6l6.85-6.85C35.9 2.38 30.47 0 24 0 14.62 0 6.51 5.38 2.56 13.22l7.98 6.19C12.43 13.72 17.74 9.5 24 9.5z"/>
            <path fill="#4285F4" d="M46.98 24.55c0-1.57-.15-3.09-.38-4.55H24v9.02h12.94c-.58 2.96-2.26 5.48-4.78 7.18l7.73 6c4.51-4.18 7.09-10.36 7.09-17.65z"/>
            <path fill="#FBBC05" d="M10.53 28.59c-.48-1.45-.76-2.99-.76-4.59s.27-3.14.76-4.59l-7.98-6.19C.92 16.46 0 20.12 0 24c0 3.88.92 7.54 2.56 10.78l7.97-6.19z"/>
            <path fill="#34A853" d="M24 48c6.48 0 11.93-2.13 15.89-5.81l-7.73-6c-2.15 1.45-4.92 2.3-8.16 2.3-6.26 0-11.57-4.22-13.47-9.91l-7.98 6.19C6.51 42.62 14.62 48 24 48z"/>
        </svg>
    );
}
